<template>
  <div
    class="company-edit-page"
    :class="isDarkTheme ? 'theme-dark' : 'theme-light'"
  >
    <!-- =====================================================
         HEADER
    ====================================================== -->

    <div class="header-slot">
      <DefaultHeader
        :title="company?.name
          ? `${company.name} · Редактирование`
          : 'Компания'"
        class="static-header"
        :offset="680"
      />
    </div>


    <!-- =====================================================
         ONLY THIS AREA SCROLLS
    ====================================================== -->

    <div
      ref="contentScrollRef"
      class="content-scroll"
    >

      <!-- ===================================================
           LOADING
      ==================================================== -->

    <div
      v-if="isLoading"
      class="page-shell loading-shell"
    >
      <div class="hero-skeleton skeleton"></div>

      <div class="loading-grid">
        <div class="card-skeleton skeleton"></div>
        <div class="card-skeleton skeleton"></div>
      </div>

      <div class="wide-skeleton skeleton"></div>
    </div>


    <!-- =====================================================
         PAGE
    ====================================================== -->

    <main
      v-else-if="company"
      class="page-shell"
    >
      <!-- ===================================================
           INTRO
      ==================================================== -->

      <section class="page-intro">
        <div>
          <div class="eyebrow">
            ПРОФИЛЬ КОМПАНИИ
          </div>

          <h1>
            Редактирование компании
          </h1>

          <p>
            Управляйте информацией, которую кандидаты
            видят в профиле вашей компании.
          </p>
        </div>

        <div
          class="access-indicator"
          :class="{
            granted: company.isAllowEditing,
            requested:
              company.isEditingRequestSent &&
              !company.isAllowEditing,
            locked:
              !company.isAllowEditing &&
              !company.isEditingRequestSent
          }"
        >
          <span class="access-dot"></span>

          {{
            company.isAllowEditing
              ? "Редактирование доступно"
              : company.isEditingRequestSent
                ? "Запрос рассматривается"
                : "Требуется доступ"
          }}
        </div>
      </section>


      <!-- ===================================================
           HERO
      ==================================================== -->

      <section class="company-hero">

        <div class="cover-area">

          <img
            v-if="company.urlOfBackground"
            :src="company.urlOfBackground"
            alt=""
            class="cover-image"
            :class="{ visible: isBackgroundLoaded }"
            @load="isBackgroundLoaded = true"
          />

          <div
            v-else
            class="cover-placeholder"
          >
            <div class="cover-shape shape-one"></div>
            <div class="cover-shape shape-two"></div>
          </div>

          <div class="cover-overlay"></div>


          <input
            ref="bannerInputRef"
            type="file"
            accept="image/*"
            hidden
            @change="onBannerPicked"
          />


          <button
            type="button"
            class="cover-edit-button"
            :class="{ disabled: !isEditingAllowed }"
            :disabled="bannerUploading"
            @click="onBannerClick"
          >
            <span
              v-if="bannerUploading"
              class="small-spinner"
            ></span>

            <svg
              v-else
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M4 16.5V20H7.5L18.2 9.3L14.7 5.8L4 16.5Z"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linejoin="round"
              />

              <path
                d="M13.8 6.7L17.3 10.2"
                stroke="currentColor"
                stroke-width="1.7"
              />
            </svg>

            {{
              bannerUploading
                ? "Загружаем..."
                : "Изменить обложку"
            }}
          </button>

        </div>


        <div class="hero-content">

          <!-- LOGO -->

          <div
            class="company-logo-shell"
            :class="{
              clickable: isEditingAllowed,
              uploading: logoUploading
            }"
            @click="onLogoTap"
          >
            <img
              v-if="company.urlOfLogo"
              :src="company.urlOfLogo"
              alt=""
              class="company-logo"
            />

            <div
              v-else
              class="company-logo-placeholder"
            >
              {{ companyInitials }}
            </div>


            <div
              v-if="isEditingAllowed"
              class="logo-edit-button"
            >
              <span
                v-if="logoUploading"
                class="tiny-spinner"
              ></span>

              <svg
                v-else
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M4 16.5V20H7.5L18.2 9.3L14.7 5.8L4 16.5Z"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
          </div>


          <input
            ref="logoInput"
            type="file"
            accept="image/*"
            class="hidden"
            @change="onLogoPicked"
          />


          <!-- COMPANY INFO -->

          <div class="hero-info">

            <div class="hero-title-row">

              <div class="hero-name-block">
                <h2>
                  {{ company.name || "Название компании" }}
                </h2>

                <div class="hero-meta">

                  <button
                    type="button"
                    class="location-pill"
                    @click="openCompanyRegionDrawer"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M12 21C12 21 18 15.5 18 10A6 6 0 1 0 6 10C6 15.5 12 21 12 21Z"
                        stroke="currentColor"
                        stroke-width="1.6"
                      />

                      <circle
                        cx="12"
                        cy="10"
                        r="2"
                        stroke="currentColor"
                        stroke-width="1.6"
                      />
                    </svg>

                    {{ getRegionNameById(company.regionId) }}
                  </button>


                  <span
                    v-if="company.specifics"
                    class="specifics-pill"
                  >
                    {{ company.specifics }}
                  </span>

                </div>
              </div>


              <!-- ACCESS -->

              <button
                type="button"
                class="editing-button"
                :class="{
                  granted: company.isAllowEditing,
                  requested:
                    company.isEditingRequestSent &&
                    !company.isAllowEditing
                }"
                :disabled="
                  isLoadingSubscription ||
                  company.isEditingRequestSent ||
                  company.isAllowEditing
                "
                @click="sendEditRequest"
              >
                <span
                  v-if="isLoadingSubscription"
                  class="button-spinner"
                ></span>

                <template v-else>

                  <svg
                    v-if="company.isAllowEditing"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M5 12.5L9.2 16.5L19 6.5"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>

                  <svg
                    v-else-if="company.isEditingRequestSent"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="8"
                      stroke="currentColor"
                      stroke-width="1.6"
                    />

                    <path
                      d="M12 7.5V12L15 14"
                      stroke="currentColor"
                      stroke-width="1.6"
                      stroke-linecap="round"
                    />
                  </svg>

                  <svg
                    v-else
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M4 16.5V20H7.5L18.2 9.3L14.7 5.8L4 16.5Z"
                      stroke="currentColor"
                      stroke-width="1.7"
                      stroke-linejoin="round"
                    />
                  </svg>

                  {{
                    company.isAllowEditing
                      ? "Редактирование разрешено"
                      : company.isEditingRequestSent
                        ? "Запрос отправлен"
                        : "Запросить редактирование"
                  }}

                </template>
              </button>

            </div>

          </div>

        </div>

      </section>


      <!-- ===================================================
           NO ACCESS INFO
      ==================================================== -->

      <section
        v-if="!isEditingAllowed"
        class="access-message"
      >
        <div class="access-message-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
          >
            <rect
              x="5"
              y="10"
              width="14"
              height="10"
              rx="3"
              stroke="currentColor"
              stroke-width="1.6"
            />

            <path
              d="M8.5 10V7.5C8.5 5.57 10.07 4 12 4C13.93 4 15.5 5.57 15.5 7.5V10"
              stroke="currentColor"
              stroke-width="1.6"
            />
          </svg>
        </div>

        <div>
          <strong>
            {{
              company.isEditingRequestSent
                ? "Запрос на редактирование уже отправлен"
                : "Редактирование пока недоступно"
            }}
          </strong>

          <span>
            {{
              company.isEditingRequestSent
                ? "После подтверждения вы сможете изменять данные и изображения компании."
                : "Запросите доступ, чтобы управлять профилем этой компании."
            }}
          </span>
        </div>
      </section>


      <!-- ===================================================
           MAIN INFO
      ==================================================== -->

      <section class="dashboard-card main-info-card">

        <div class="card-heading">

          <div>
            <span class="card-label">
              ПРОФИЛЬ
            </span>

            <h3>
              Основная информация
            </h3>

            <p>
              Эти данные отображаются кандидатам
              в публичном профиле компании.
            </p>
          </div>


          <div class="section-icon blue">
            <svg
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M4 20V7L12 3L20 7V20"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linejoin="round"
              />

              <path
                d="M8 20V11H16V20"
                stroke="currentColor"
                stroke-width="1.6"
              />
            </svg>
          </div>

        </div>


        <div class="form-grid">

          <label class="form-field">
            <span>
              Специализация
            </span>

            <input
              v-model="company.specifics"
              type="text"
              placeholder="Например, IT / Retail / Design"
              :disabled="!isEditingAllowed"
            />
          </label>


          <label class="form-field">
            <span>
              Email
            </span>

            <input
              v-model="company.email"
              type="email"
              placeholder="hello@company.com"
              :disabled="!isEditingAllowed"
            />
          </label>


          <label class="form-field full">
            <span>
              Website
            </span>

            <input
              v-model="company.website"
              type="text"
              placeholder="https://company.com"
              :disabled="!isEditingAllowed"
            />
          </label>


          <label class="form-field full">
            <span>
              О компании
            </span>

            <textarea
              v-model="company.aboutCompany"
              rows="6"
              placeholder="Расскажите кандидатам о компании..."
              :disabled="!isEditingAllowed"
            ></textarea>
          </label>

        </div>


        <div class="card-actions">

          <span
            class="save-state"
            :class="{ changed: hasCompanyChanges }"
          >
            {{
              hasCompanyChanges
                ? "Есть несохранённые изменения"
                : "Всё сохранено"
            }}
          </span>

          <button
            type="button"
            class="primary-button"
            :disabled="
              !hasCompanyChanges ||
              !isEditingAllowed ||
              companySaving
            "
            @click="saveCompany"
          >
            <span
              v-if="companySaving"
              class="button-spinner"
            ></span>

            {{
              companySaving
                ? "Сохраняем..."
                : "Сохранить изменения"
            }}
          </button>

        </div>

      </section>


      <!-- ===================================================
           OFFICE + SOCIAL
      ==================================================== -->

      <div class="dashboard-grid">

        <!-- OFFICE -->

        <section class="dashboard-card office-card">

          <div class="card-heading">

            <div>
              <span class="card-label">
                ЛОКАЦИЯ
              </span>

              <h3>
                Офис
              </h3>

              <p>
                Адрес компании и расположение на карте.
              </p>
            </div>


            <div class="section-icon purple">
              <svg
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M12 21C12 21 18 15.5 18 10A6 6 0 1 0 6 10C6 15.5 12 21 12 21Z"
                  stroke="currentColor"
                  stroke-width="1.6"
                />

                <circle
                  cx="12"
                  cy="10"
                  r="2"
                  stroke="currentColor"
                  stroke-width="1.6"
                />
              </svg>
            </div>

          </div>


          <div class="current-address">

            <div class="address-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M5 20V9L12 5L19 9V20"
                  stroke="currentColor"
                  stroke-width="1.6"
                />
              </svg>
            </div>

            <div>
              <span>
                Текущий адрес
              </span>

              <strong>
                {{
                  company.companyLocation?.address ||
                  "Адрес не указан"
                }}
              </strong>
            </div>

          </div>


          <div class="map-container">
            <ChooseGoogleMap
              :lat="company.companyLocation?.latitude"
              :lng="company.companyLocation?.longitude"
              :isEnabled="isEditingAllowed"
              :companyId="company.id"
              @locationSelected="onNewLocation"
            />
          </div>

        </section>


        <!-- SOCIAL -->

        <section
          class="dashboard-card social-card"
          :class="{ 'disabled-card': !isEditingAllowed }"
        >

          <div class="card-heading">

            <div>
              <span class="card-label">
                КОНТАКТЫ
              </span>

              <h3>
                Социальные сети
              </h3>

              <p>
                Добавьте официальные страницы компании.
              </p>
            </div>


            <div class="section-icon green">
              <svg
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  cx="6"
                  cy="12"
                  r="2.5"
                  stroke="currentColor"
                  stroke-width="1.6"
                />

                <circle
                  cx="18"
                  cy="6"
                  r="2.5"
                  stroke="currentColor"
                  stroke-width="1.6"
                />

                <circle
                  cx="18"
                  cy="18"
                  r="2.5"
                  stroke="currentColor"
                  stroke-width="1.6"
                />

                <path
                  d="M8.2 10.8L15.7 7.2M8.2 13.2L15.7 16.8"
                  stroke="currentColor"
                  stroke-width="1.6"
                />
              </svg>
            </div>

          </div>


          <div class="social-grid">

            <label
              v-for="item in SOCIAL_ICONS_EDIT"
              :key="item.key"
              class="social-field"
            >
              <div
                class="social-icon-shell"
                :class="{
                  active:
                    company.social?.[item.key]?.length > 0
                }"
              >
                <img
                  :src="item.icon"
                  alt=""
                />
              </div>

              <input
                v-model="company.social[item.key]"
                type="text"
                :placeholder="item.placeholder"
                :disabled="!isEditingAllowed"
              />
            </label>

          </div>


          <div class="card-actions">

            <span
              class="save-state"
              :class="{ changed: hasSocialChanges }"
            >
              {{
                hasSocialChanges
                  ? "Есть несохранённые изменения"
                  : "Всё сохранено"
              }}
            </span>

            <button
              type="button"
              class="secondary-button"
              :disabled="
                !hasSocialChanges ||
                !isEditingAllowed ||
                socialSaving
              "
              @click="saveSocial"
            >
              <span
                v-if="socialSaving"
                class="button-spinner dark"
              ></span>

              {{
                socialSaving
                  ? "Сохраняем..."
                  : "Сохранить"
              }}
            </button>

          </div>

        </section>

      </div>


      <!-- ===================================================
           EXTRA PHOTOS
      ==================================================== -->

      <section
        class="dashboard-card wide-card gallery-card"
        :class="{ 'disabled-card': !isEditingAllowed }"
      >

        <div class="card-heading">

          <div>
            <span class="card-label">
              ГАЛЕРЕЯ
            </span>

            <h3>
              Дополнительные фото
            </h3>

            <p>
              Покажите кандидатам офис, команду
              или атмосферу вашей компании.
            </p>
          </div>


          <div class="section-icon blue">
            <svg
              viewBox="0 0 24 24"
              fill="none"
            >
              <rect
                x="4"
                y="5"
                width="16"
                height="14"
                rx="3"
                stroke="currentColor"
                stroke-width="1.6"
              />

              <circle
                cx="9"
                cy="10"
                r="1.5"
                stroke="currentColor"
                stroke-width="1.5"
              />

              <path
                d="M6.5 17L11 13L14 15.5L16.5 13.5L19 16"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

        </div>


        <div class="images-row">

          <button
            v-for="i in 3"
            :key="i"
            type="button"
            class="image-slot"
            :class="{
              filled: extraImages[i - 1],
              disabled: !isEditingAllowed
            }"
            @click="
              isEditingAllowed
                ? selectExtraImage(i - 1)
                : null
            "
          >

            <div
              class="img-wrapper"
              :class="{
                deleting:
                  extraDeleting[i - 1]
              }"
            >

              <img
                v-if="extraImages[i - 1]"
                :src="extraImages[i - 1]"
                class="slot-img"
                alt=""
              />


              <div
                v-else
                class="slot-placeholder"
              >
                <div class="plus">
                  +
                </div>

                <span>
                  Добавить фото
                </span>
              </div>


              <button
                v-if="
                  extraImages[i - 1] &&
                  isEditingAllowed
                "
                type="button"
                class="delete-btn"
                @click.stop="
                  removeExtraImage(i - 1)
                "
              >
                <span
                  v-if="extraDeleting[i - 1]"
                  class="mini-loader"
                ></span>

                <svg
                  v-else
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M6 6L18 18M18 6L6 18"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                  />
                </svg>
              </button>

            </div>


            <div
              v-if="extraUploading[i - 1]"
              class="image-upload-overlay"
            >
              <span class="button-spinner"></span>
            </div>

          </button>

        </div>


        <input
          ref="extraInput"
          type="file"
          accept="image/*"
          class="hidden"
          @change="onExtraPicked"
        />

      </section>


      <!-- ===================================================
           PERKS
      ==================================================== -->

      <section
        class="dashboard-card wide-card perks-card"
        :class="{ 'disabled-card': !isEditingAllowed }"
      >

        <div class="card-heading">

          <div>
            <span class="card-label">
              EMPLOYER BRAND
            </span>

            <h3>
              Преимущества компании
            </h3>

            <p>
              Отметьте условия и преимущества,
              которые вы предлагаете сотрудникам.
            </p>
          </div>


          <div class="section-icon orange">
            <svg
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M12 21S4 16.5 4 10.5A4.5 4.5 0 0 1 12 7.7A4.5 4.5 0 0 1 20 10.5C20 16.5 12 21 12 21Z"
                stroke="currentColor"
                stroke-width="1.6"
              />
            </svg>
          </div>

        </div>


        <div class="perk-grid">

          <button
            v-for="perk in ALL_BENEFITS"
            :key="perk.id"
            type="button"
            class="perk"
            :class="{
              selected:
                selectedPerks.includes(perk.id)
            }"
            :disabled="!isEditingAllowed"
            @click="
              isEditingAllowed
                ? togglePerk(perk.id)
                : null
            "
          >
            <span
              class="perk-check"
              :class="{
                visible:
                  selectedPerks.includes(perk.id)
              }"
            >
              ✓
            </span>

            {{ perk.ruName }}
          </button>

        </div>


        <div class="card-actions">

          <span
            class="save-state"
            :class="{ changed: hasPerksChanges }"
          >
            {{
              hasPerksChanges
                ? "Список изменён"
                : "Всё сохранено"
            }}
          </span>

          <button
            type="button"
            class="secondary-button"
            :disabled="
              !hasPerksChanges ||
              !isEditingAllowed ||
              perksSaving
            "
            @click="savePerks"
          >
            <span
              v-if="perksSaving"
              class="button-spinner dark"
            ></span>

            {{
              perksSaving
                ? "Сохраняем..."
                : "Сохранить преимущества"
            }}
          </button>

        </div>

      </section>

    </main>

    </div>


    <!-- =====================================================
         STATIC FOOTER
    ====================================================== -->

    <div class="footer-slot">
      <Footer class="static-footer" />
    </div>


    <!-- =====================================================
         ACCESS REQUEST
    ====================================================== -->

    <EditAccessRequestModal
      v-if="showRequestModal"
      :companyId="company?.id || company?.companyId"
      @close="showRequestModal = false"
      @success="onRequestSuccess"
    />


    <!-- =====================================================
         SNACKBAR
    ====================================================== -->

    <Snackbar
      :message="snackbarMessage"
      :show="showSnackbar"
      :type="snackbarType"
    />


    <!-- =====================================================
         REGION
    ====================================================== -->

    <CompanyRegionDrawer
      v-if="company && showCompanyRegionDrawer"
      :visible="showCompanyRegionDrawer"
      :modelValue="company.regionId"
      :companyId="company.id"
      @update:modelValue="onCompanyRegionSelected"
      @close="showCompanyRegionDrawer = false"
    />


  </div>
</template>


<script setup>

import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref
} from "vue";

import { useRoute } from "vue-router";

import DefaultHeader from "../components/DefaultHeader.vue";
import Footer from "../components/MiniFooter.vue";
import Snackbar from "../components/Snackbar.vue";
import ChooseGoogleMap from "../components/ChooseGoogleMap.vue";
import EditAccessRequestModal from "../components/EditAccessRequestModal.vue";
import CompanyRegionDrawer from "../components/CompanyRegionDrawer.vue";

import CompanyService from "../api/CompanyService.js";
import RegionService from "../utils/RegionService.js";

import facebookIcon from "../assets/facebook-profile.png";
import instagramIcon from "../assets/instagram-profile.png";
import linkedinIcon from "../assets/linkedin-profile.png";
import twitterIcon from "../assets/x-profile.png";
import telegramIcon from "../assets/telegram-profile.png";
import youtubeIcon from "../assets/youtube-profile.png";


/* =========================================================
   ROUTE + THEME
========================================================= */

const route = useRoute();

const isDarkTheme = ref(false);

let themeObserver = null;


function updateLocalTheme() {
  isDarkTheme.value =
    document.documentElement
      .getAttribute("data-theme") === "dark";
}


/* =========================================================
   MAIN STATE
========================================================= */

const company = ref(null);
const originalCompany = ref({});

const isLoading = ref(true);
const isBackgroundLoaded = ref(false);

const showRequestModal = ref(false);
const showCompanyRegionDrawer = ref(false);

const isEditingAllowed = ref(false);
const isLoadingSubscription = ref(false);

const companySaving = ref(false);
const socialSaving = ref(false);
const perksSaving = ref(false);

const bannerUploading = ref(false);
const logoUploading = ref(false);


/* =========================================================
   INPUTS
========================================================= */

const contentScrollRef = ref(null);
const bannerInputRef = ref(null);
const logoInput = ref(null);
const extraInput = ref(null);


/* =========================================================
   SNACKBAR
========================================================= */

const showSnackbar = ref(false);
const snackbarMessage = ref("");
const snackbarType = ref("success");

let snackbarTimer = null;


function showMessage(
  message,
  type = "success"
) {
  snackbarMessage.value = message;
  snackbarType.value = type;
  showSnackbar.value = true;

  if (snackbarTimer) {
    clearTimeout(snackbarTimer);
  }

  snackbarTimer =
    window.setTimeout(
      () => {
        showSnackbar.value = false;
      },
      3200
    );
}


/* =========================================================
   SOCIAL
========================================================= */

const SOCIAL_ICONS_EDIT = [
  {
    key: "facebook",
    icon: facebookIcon,
    placeholder: "Facebook"
  },
  {
    key: "instagram",
    icon: instagramIcon,
    placeholder: "Instagram"
  },
  {
    key: "linkedin",
    icon: linkedinIcon,
    placeholder: "LinkedIn"
  },
  {
    key: "twitter",
    icon: twitterIcon,
    placeholder: "x.com"
  },
  {
    key: "telegram",
    icon: telegramIcon,
    placeholder: "Telegram"
  },
  {
    key: "youtube",
    icon: youtubeIcon,
    placeholder: "YouTube"
  }
];


/* =========================================================
   BENEFITS
========================================================= */

const ALL_BENEFITS = [
  { id: "01", ruName: "Оплачиваемые отгулы" },
  { id: "02", ruName: "Гибкий график работы" },
  { id: "03", ruName: "Абонемент в спортзал" },
  { id: "04", ruName: "Укороченные дни перед праздниками" },
  { id: "05", ruName: "Оплачиваемый декрет" },
  { id: "06", ruName: "Холодильник с едой" },
  { id: "07", ruName: "Различные мероприятия" },
  { id: "08", ruName: "Волонтерские дни" },
  { id: "09", ruName: "Pet-friendly офис" },
  { id: "10", ruName: "Оплачиваемые обучения" },
  { id: "11", ruName: "Удаленка" },
  { id: "12", ruName: "Оплачиваемый проезд" },
  { id: "13", ruName: "Помощь по уходу за детьми" },
  { id: "14", ruName: "Программа переквалификации" },
  { id: "15", ruName: "Игровая комната" },
  { id: "16", ruName: "Медицинская страховка" },
  { id: "17", ruName: "Внутренние сервисы" }
];

const selectedPerks = ref([]);


/* =========================================================
   EXTRA IMAGES
========================================================= */

const extraImages = ref([]);
const extraUploading = ref([
  false,
  false,
  false
]);

const extraDeleting = ref([
  false,
  false,
  false
]);

let extraIndex = null;


/* =========================================================
   COMPUTED
========================================================= */

const companyInitials =
  computed(
    () => {
      const name =
        company.value
          ?.name
          ?.trim();

      if (!name) {
        return "HR";
      }

      return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(word => word[0])
        .join("")
        .toUpperCase();
    }
  );


const hasCompanyChanges =
  computed(
    () => {
      if (
        !company.value ||
        !originalCompany.value
      ) {
        return false;
      }

      const fields = [
        "specifics",
        "email",
        "website",
        "aboutCompany"
      ];

      return fields.some(
        field =>
          (
            company.value[field] ||
            ""
          ) !==
          (
            originalCompany.value[field] ||
            ""
          )
      );
    }
  );


const hasSocialChanges =
  computed(
    () => {
      if (
        !company.value ||
        !originalCompany.value
      ) {
        return false;
      }

      const keys = [
        "facebook",
        "instagram",
        "linkedin",
        "twitter",
        "telegram",
        "youtube"
      ];

      return keys.some(
        key =>
          (
            company.value.social?.[key] ||
            ""
          ) !==
          (
            originalCompany.value.social?.[key] ||
            ""
          )
      );
    }
  );


const hasPerksChanges =
  computed(
    () => {
      const original =
        (
          originalCompany.value
            ?.perksAndBenefits ||
          []
        ).map(
          perk =>
            perk?.id ||
            perk
        );

      return (
        JSON.stringify(original) !==
        JSON.stringify(selectedPerks.value)
      );
    }
  );


/* =========================================================
   HELPERS
========================================================= */

function getRegionNameById(
  regionId
) {
  return (
    RegionService.handleRegion(
      regionId
    ) ||
    "Регион не указан"
  );
}


function fileToBase64(
  file
) {
  return new Promise(
    (
      resolve,
      reject
    ) => {
      const reader =
        new FileReader();

      reader.onload =
        () =>
          resolve(
            reader.result
          );

      reader.onerror =
        reject;

      reader.readAsDataURL(
        file
      );
    }
  );
}


/* =========================================================
   LOAD
========================================================= */

onMounted(
  async () => {
    updateLocalTheme();

    themeObserver =
      new MutationObserver(
        updateLocalTheme
      );

    themeObserver.observe(
      document.documentElement,
      {
        attributes: true,
        attributeFilter: [
          "data-theme"
        ]
      }
    );


    requestAnimationFrame(() => {
      if (contentScrollRef.value) {
        contentScrollRef.value.scrollTop = 0;
      }
    });


    const companyId =
      route.params.companyId;

    try {
      const data =
        await CompanyService
          .getCompanyWithEditingOptions(
            companyId,
            true
          );


      company.value = data;


      const social =
        data.social || {};


      company.value.social = {
        facebook:
          social.facebook_id ??
          social.facebook ??
          "",

        instagram:
          social.instagram_id ??
          social.instagram ??
          "",

        linkedin:
          social.linkedin_id ??
          social.linkedin ??
          "",

        twitter:
          social.twitter_id ??
          social.twitter ??
          "",

        telegram:
          social.telegram_id ??
          social.telegram ??
          "",

        youtube:
          social.youtube_id ??
          social.youtube ??
          ""
      };


      selectedPerks.value =
        (
          company.value
            .perksAndBenefits ||
          []
        ).map(
          perk =>
            perk?.id ||
            perk
        );


      company.value
        .perksAndBenefits = [
          ...selectedPerks.value
        ];


      extraImages.value = [];

      if (
        Array.isArray(
          data.picturesOfCompany
        )
      ) {
        extraImages.value =
          data.picturesOfCompany
            .map(
              item =>
                item?.image_url ||
                ""
            );
      }


      while (
        extraImages.value.length <
        3
      ) {
        extraImages.value.push("");
      }


      extraImages.value =
        extraImages.value.slice(
          0,
          3
        );


      isEditingAllowed.value =
        data.isAllowEditing ===
        true;


      originalCompany.value =
        JSON.parse(
          JSON.stringify(
            company.value
          )
        );

    } catch (error) {
      console.error(
        "❌ Ошибка при загрузке компании:",
        error
      );

      showMessage(
        "Не удалось загрузить компанию",
        "error"
      );
    } finally {
      isLoading.value = false;
    }
  }
);


onBeforeUnmount(
  () => {
    themeObserver
      ?.disconnect();

    if (snackbarTimer) {
      clearTimeout(
        snackbarTimer
      );
    }
  }
);


/* =========================================================
   ACCESS
========================================================= */

function sendEditRequest() {
  if (
    company.value
      ?.isAllowEditing ||
    company.value
      ?.isEditingRequestSent
  ) {
    return;
  }

  showRequestModal.value =
    true;
}


function onRequestSuccess() {
  showRequestModal.value =
    false;

  if (company.value) {
    company.value
      .isEditingRequestSent =
        true;
  }

  showMessage(
    "Запрос на редактирование отправлен"
  );
}


function denyEdit() {
  showMessage(
    "Редактирование компании пока недоступно",
    "error"
  );
}


/* =========================================================
   LOGO
========================================================= */

function onLogoTap() {
  if (!isEditingAllowed.value) {
    denyEdit();
    return;
  }

  if (logoUploading.value) {
    return;
  }

  logoInput.value
    ?.click();
}


async function onLogoPicked(
  event
) {
  const file =
    event.target.files?.[0];

  if (!file) {
    return;
  }


  logoUploading.value =
    true;

  try {
    const url =
      await CompanyService
        .uploadCompanyImage(
          file,
          "logo"
        );

    company.value
      .urlOfLogo =
        url;

    showMessage(
      "Логотип обновлён"
    );

  } catch (error) {
    console.error(
      "❌ Ошибка загрузки логотипа:",
      error
    );

    showMessage(
      "Не удалось загрузить логотип",
      "error"
    );
  } finally {
    logoUploading.value =
      false;

    event.target.value =
      "";
  }
}


/* =========================================================
   BANNER
========================================================= */

function onBannerClick() {
  if (!isEditingAllowed.value) {
    denyEdit();
    return;
  }

  if (bannerUploading.value) {
    return;
  }

  bannerInputRef.value
    ?.click();
}


async function onBannerPicked(
  event
) {
  const file =
    event.target.files?.[0];

  if (!file) {
    return;
  }


  bannerUploading.value =
    true;

  try {
    const url =
      await CompanyService
        .uploadCompanyImageWithRenew(
          file,
          "background",
          company.value.id
        );

    isBackgroundLoaded.value =
      false;

    company.value
      .urlOfBackground =
        url;

    showMessage(
      "Обложка обновлена"
    );

  } catch (error) {
    console.error(
      "❌ Ошибка загрузки обложки:",
      error
    );

    showMessage(
      "Не удалось загрузить обложку",
      "error"
    );
  } finally {
    bannerUploading.value =
      false;

    event.target.value =
      "";
  }
}


/* =========================================================
   REGION
========================================================= */

function openCompanyRegionDrawer() {
  if (!isEditingAllowed.value) {
    denyEdit();
    return;
  }

  showCompanyRegionDrawer.value =
    true;
}


function onCompanyRegionSelected(
  value
) {
  if (!company.value) {
    return;
  }


  if (
    typeof value === "object" &&
    value !== null
  ) {
    company.value.regionId =
      value.id ??
      value.regionId ??
      company.value.regionId;
  } else {
    company.value.regionId =
      value;
  }


  showCompanyRegionDrawer.value =
    false;
}


/* =========================================================
   COMPANY DATA
========================================================= */

async function saveCompany() {
  if (
    !hasCompanyChanges.value ||
    !isEditingAllowed.value ||
    companySaving.value
  ) {
    return;
  }


  companySaving.value =
    true;

  try {
    const payload = {
      specifics:
        company.value.specifics ||
        null,

      email:
        company.value.email ||
        null,

      website:
        company.value.website ||
        null,

      aboutCompany:
        company.value.aboutCompany ||
        null
    };


    await CompanyService
      .updateCompany(
        company.value.id,
        payload
      );


    originalCompany.value =
      JSON.parse(
        JSON.stringify(
          company.value
        )
      );


    showMessage(
      "Изменения сохранены"
    );

  } catch (error) {
    console.error(
      "❌ Ошибка сохранения компании:",
      error
    );

    showMessage(
      "Не удалось сохранить изменения",
      "error"
    );
  } finally {
    companySaving.value =
      false;
  }
}


/* =========================================================
   LOCATION
========================================================= */

function onNewLocation({
  lat,
  lng,
  address
}) {
  if (!company.value) {
    return;
  }


  company.value.companyLocation = {
    latitude: lat,
    longitude: lng,
    address
  };


  showMessage(
    "Адрес компании обновлён"
  );
}


/* =========================================================
   SOCIAL
========================================================= */

async function saveSocial() {
  if (
    !hasSocialChanges.value ||
    !isEditingAllowed.value ||
    socialSaving.value
  ) {
    return;
  }


  socialSaving.value =
    true;

  try {
    const social =
      company.value.social;


    const payload = {
      social_data: {
        facebookId:
          social.facebook ||
          null,

        facebookEnabled:
          !!social.facebook,

        instagramId:
          social.instagram ||
          null,

        instagramEnabled:
          !!social.instagram,

        linkedinId:
          social.linkedin ||
          null,

        linkedinEnabled:
          !!social.linkedin,

        twitterId:
          social.twitter ||
          null,

        twitterEnabled:
          !!social.twitter,

        telegramId:
          social.telegram ||
          null,

        telegramEnabled:
          !!social.telegram,

        youtubeId:
          social.youtube ||
          null,

        youtubeEnabled:
          !!social.youtube
      }
    };


    await CompanyService
      .updateCompany(
        company.value.id,
        payload
      );


    originalCompany.value.social =
      JSON.parse(
        JSON.stringify(
          company.value.social
        )
      );


    showMessage(
      "Социальные сети обновлены"
    );

  } catch (error) {
    console.error(
      "❌ Ошибка сохранения соцсетей:",
      error
    );

    showMessage(
      "Не удалось сохранить соцсети",
      "error"
    );
  } finally {
    socialSaving.value =
      false;
  }
}


/* =========================================================
   EXTRA IMAGES
========================================================= */

function selectExtraImage(
  index
) {
  if (!isEditingAllowed.value) {
    denyEdit();
    return;
  }

  extraIndex =
    index;

  extraInput.value
    ?.click();
}


async function onExtraPicked(
  event
) {
  const file =
    event.target.files?.[0];

  if (
    !file ||
    extraIndex === null
  ) {
    return;
  }


  const index =
    extraIndex;

  const oldPreview =
    extraImages.value[index];


  extraUploading.value[index] =
    true;


  try {
    extraImages.value[index] =
      await fileToBase64(
        file
      );


    const url =
      await CompanyService
        .uploadCompanyExtraImagesWithRenew(
          file,
          "extra",
          true,
          company.value.id
        );


    if (
      !Array.isArray(
        company.value
          .picturesOfCompany
      )
    ) {
      company.value
        .picturesOfCompany =
          [];
    }


    company.value
      .picturesOfCompany[index] = {
        id:
          company.value
            .picturesOfCompany
            ?.[index]
            ?.id ||
          null,

        image_url: url
      };


    extraImages.value[index] =
      url;


    showMessage(
      "Фото загружено"
    );

  } catch (error) {
    console.error(
      "❌ Ошибка загрузки фото:",
      error
    );

    extraImages.value[index] =
      oldPreview;


    showMessage(
      "Не удалось загрузить фото",
      "error"
    );
  } finally {
    extraUploading.value[index] =
      false;

    extraIndex =
      null;

    event.target.value =
      "";
  }
}


async function removeExtraImage(
  index
) {
  if (!isEditingAllowed.value) {
    return;
  }


  const item =
    company.value
      .picturesOfCompany
      ?.[index];


  if (!item?.id) {
    return;
  }


  extraDeleting.value[index] =
    true;

  try {
    await CompanyService
      .updateCompany(
        company.value.id,
        {
          image_delete_id:
            item.id
        }
      );


    extraImages.value[index] =
      "";


    company.value
      .picturesOfCompany[index] =
        null;


    showMessage(
      "Фото удалено"
    );

  } catch (error) {
    console.error(
      "❌ Ошибка удаления фото:",
      error
    );

    showMessage(
      "Не удалось удалить фото",
      "error"
    );
  } finally {
    extraDeleting.value[index] =
      false;
  }
}


/* =========================================================
   PERKS
========================================================= */

function togglePerk(
  id
) {
  if (!isEditingAllowed.value) {
    return;
  }


  if (
    selectedPerks.value
      .includes(id)
  ) {
    selectedPerks.value =
      selectedPerks.value
        .filter(
          item =>
            item !== id
        );
  } else {
    selectedPerks.value.push(
      id
    );
  }


  company.value
    .perksAndBenefits = [
      ...selectedPerks.value
    ];
}


async function savePerks() {
  if (
    !hasPerksChanges.value ||
    !isEditingAllowed.value ||
    perksSaving.value
  ) {
    return;
  }


  perksSaving.value =
    true;

  try {
    const payload = {
      perks_and_benefits:
        ALL_BENEFITS
          .filter(
            perk =>
              selectedPerks.value
                .includes(
                  perk.id
                )
          )
          .map(
            perk => ({
              id:
                perk.id,

              name:
                perk.ruName,

              ru_name:
                perk.ruName,

              eng_name:
                perk.engName ||
                ""
            })
          )
    };


    await CompanyService
      .updateCompany(
        company.value.id,
        payload
      );


    originalCompany.value
      .perksAndBenefits = [
        ...selectedPerks.value
      ];


    showMessage(
      "Преимущества сохранены"
    );

  } catch (error) {
    console.error(
      "❌ Ошибка сохранения преимуществ:",
      error
    );

    showMessage(
      "Не удалось сохранить преимущества",
      "error"
    );
  } finally {
    perksSaving.value =
      false;
  }
}

</script>


<style scoped>

/* =========================================================
   LOCAL THEMES
========================================================= */

.company-edit-page.theme-light {
  --page-bg: #f4f6f9;

  --page-blue-glow:
    rgba(55, 120, 238, 0.055);

  --page-purple-glow:
    rgba(111, 83, 232, 0.045);

  --card-bg: #ffffff;
  --card-soft: #f7f8fa;
  --card-hover: #f1f4f8;

  --border:
    rgba(24, 38, 58, 0.07);

  --border-hover:
    rgba(50, 110, 195, 0.16);

  --border-strong:
    #e3e7ec;

  --divider:
    #edf0f3;

  --text:
    #252930;

  --text-secondary:
    #7f8792;

  --text-muted:
    #9ba2ad;

  --input-bg:
    #f7f8fa;

  --input-focus:
    #ffffff;

  --input-border:
    #e3e7ec;

  --input-placeholder:
    #a9afb8;

  --blue:
    #287be7;

  --blue-soft:
    rgba(40, 123, 231, 0.09);

  --purple:
    #735ed0;

  --purple-soft:
    rgba(115, 94, 208, 0.09);

  --green:
    #2c9a69;

  --green-soft:
    rgba(44, 154, 105, 0.09);

  --orange:
    #d4873d;

  --orange-soft:
    rgba(212, 135, 61, 0.10);

  --red:
    #d6535e;

  --red-soft:
    rgba(214, 83, 94, 0.09);

  --shadow:
    0 10px 32px
    rgba(29, 48, 74, 0.045);

  --shadow-large:
    0 14px 42px
    rgba(28, 47, 75, 0.065);

  --shadow-hover:
    0 16px 38px
    rgba(31, 55, 90, 0.085);

  --skeleton:
    #e7eaee;

  --skeleton-highlight:
    rgba(255, 255, 255, 0.62);
}


.company-edit-page.theme-dark {
  --page-bg: #17181b;

  --page-blue-glow:
    rgba(47, 105, 193, 0.08);

  --page-purple-glow:
    rgba(101, 76, 197, 0.06);

  --card-bg: #202125;
  --card-soft: #292b2f;
  --card-hover: #303238;

  --border:
    rgba(255, 255, 255, 0.065);

  --border-hover:
    rgba(91, 145, 218, 0.18);

  --border-strong:
    #383b41;

  --divider:
    #31343a;

  --text:
    #edf0f4;

  --text-secondary:
    #9299a4;

  --text-muted:
    #747c87;

  --input-bg:
    #292b2f;

  --input-focus:
    #2d2f34;

  --input-border:
    #383b41;

  --input-placeholder:
    #676f7b;

  --blue:
    #68a4f5;

  --blue-soft:
    rgba(69, 133, 224, 0.13);

  --purple:
    #9786ea;

  --purple-soft:
    rgba(128, 105, 221, 0.13);

  --green:
    #69c897;

  --green-soft:
    rgba(58, 174, 113, 0.12);

  --orange:
    #e1a15f;

  --orange-soft:
    rgba(212, 135, 61, 0.13);

  --red:
    #e46a74;

  --red-soft:
    rgba(214, 83, 94, 0.13);

  --shadow:
    0 12px 34px
    rgba(0, 0, 0, 0.18);

  --shadow-large:
    0 16px 45px
    rgba(0, 0, 0, 0.24);

  --shadow-hover:
    0 18px 42px
    rgba(0, 0, 0, 0.27);

  --skeleton:
    #292b30;

  --skeleton-highlight:
    rgba(255, 255, 255, 0.045);
}


/* =========================================================
   PAGE
========================================================= */

.company-edit-page {
  position: fixed;
  inset: 0;

  width: 100%;
  height: 100vh;
  height: 100dvh;

  display: flex;
  flex-direction: column;

  overflow: hidden;

  color: var(--text);

  background:
    radial-gradient(
      circle at 8% 4%,
      var(--page-blue-glow),
      transparent 400px
    ),
    radial-gradient(
      circle at 92% 7%,
      var(--page-purple-glow),
      transparent 360px
    ),
    var(--page-bg);

  transition:
    background-color 0.25s ease,
    color 0.25s ease;
}


/* =========================================================
   STATIC HEADER
========================================================= */

.header-slot {
  position: relative;
  z-index: 1000;

  flex: 0 0 auto;

  width: 100%;

  background: var(--page-bg);
}


.static-header {
  position: relative !important;
  top: auto !important;
  left: auto !important;
  right: auto !important;
  bottom: auto !important;

  width: 100%;

  transform: none !important;
}


/* =========================================================
   ONLY SCROLLABLE AREA
========================================================= */

.content-scroll {
  position: relative;

  flex: 1 1 auto;
  min-height: 0;

  width: 100%;

  overflow-y: auto;
  overflow-x: hidden;

  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;

  scrollbar-gutter: stable;

  background: transparent;
}


.page-shell {
  width:
    min(
      1080px,
      calc(100% - 40px)
    );

  margin:
    0 auto;

  padding:
    28px
    0
    32px;

  box-sizing: border-box;
}


/* =========================================================
   STATIC FOOTER
========================================================= */

.footer-slot {
  position: relative;
  z-index: 1000;

  flex: 0 0 auto;

  width: 100%;
}


.static-footer {
  position: relative !important;
  top: auto !important;
  left: auto !important;
  right: auto !important;
  bottom: auto !important;

  width: 100%;

  transform: none !important;
}


/* =========================================================
   INTRO
========================================================= */

.page-intro {
  display: flex;

  align-items:
    flex-start;

  justify-content:
    space-between;

  gap: 40px;

  margin-bottom:
    28px;
}


.eyebrow {
  margin-bottom:
    7px;

  color:
    var(--text-muted);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    9px;

  letter-spacing:
    0.12em;

  text-transform:
    uppercase;
}


.page-intro h1 {
  margin: 0;

  color:
    var(--text);

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size:
    30px;

  line-height:
    1.15;

  letter-spacing:
    -0.03em;
}


.page-intro p {
  max-width:
    580px;

  margin:
    10px
    0
    0;

  color:
    var(--text-secondary);

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    12px;

  line-height:
    1.6;
}


/* =========================================================
   ACCESS INDICATOR
========================================================= */

.access-indicator {
  flex-shrink: 0;

  min-height:
    36px;

  display:
    inline-flex;

  align-items:
    center;

  gap:
    8px;

  padding:
    0
    13px;

  color:
    var(--orange);

  background:
    var(--orange-soft);

  border:
    1px solid
    rgba(212, 135, 61, 0.11);

  border-radius:
    11px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    9px;
}


.access-indicator.granted {
  color:
    var(--green);

  background:
    var(--green-soft);

  border-color:
    rgba(44, 154, 105, 0.12);
}


.access-indicator.requested {
  color:
    var(--blue);

  background:
    var(--blue-soft);

  border-color:
    rgba(40, 123, 231, 0.12);
}


.access-indicator.locked {
  color:
    var(--orange);
}


.access-dot {
  width:
    6px;

  height:
    6px;

  flex-shrink:
    0;

  background:
    currentColor;

  border-radius:
    50%;
}


/* =========================================================
   HERO
========================================================= */

.company-hero {
  overflow:
    hidden;

  margin-bottom:
    22px;

  background:
    var(--card-bg);

  border:
    1px solid
    var(--border);

  border-radius:
    24px;

  box-shadow:
    var(--shadow-large);

  transition:
    background-color 0.25s ease,
    border-color 0.18s ease,
    box-shadow 0.18s ease;
}


.company-hero:hover {
  border-color:
    var(--border-hover);
}


/* =========================================================
   COVER
========================================================= */

.cover-area {
  position:
    relative;

  height:
    260px;

  overflow:
    hidden;

  background:
    var(--card-soft);
}


.cover-image,
.cover-placeholder {
  width:
    100%;

  height:
    100%;
}


.cover-image {
  display:
    block;

  object-fit:
    cover;

  opacity:
    0;

  transition:
    opacity 0.4s ease;
}


.cover-image.visible {
  opacity:
    1;
}


.cover-placeholder {
  position:
    relative;

  overflow:
    hidden;

  background:
    linear-gradient(
      120deg,
      var(--card-soft),
      var(--blue-soft)
    );
}


.cover-shape {
  position:
    absolute;

  border-radius:
    50%;
}


.shape-one {
  width:
    420px;

  height:
    420px;

  top:
    -250px;

  right:
    70px;

  background:
    var(--blue-soft);
}


.shape-two {
  width:
    330px;

  height:
    330px;

  left:
    80px;

  bottom:
    -230px;

  background:
    var(--purple-soft);
}


.cover-overlay {
  position:
    absolute;

  inset:
    0;

  pointer-events:
    none;

  background:
    linear-gradient(
      180deg,
      transparent 45%,
      rgba(7, 14, 25, 0.20)
    );
}


/* =========================================================
   COVER BUTTON
========================================================= */

.cover-edit-button {
  position:
    absolute;

  right:
    18px;

  bottom:
    18px;

  min-height:
    38px;

  display:
    inline-flex;

  align-items:
    center;

  justify-content:
    center;

  gap:
    7px;

  padding:
    0
    13px;

  color:
    #ffffff;

  background:
    rgba(17, 23, 32, 0.56);

  border:
    1px solid
    rgba(255, 255, 255, 0.14);

  border-radius:
    11px;

  backdrop-filter:
    blur(14px);

  -webkit-backdrop-filter:
    blur(14px);

  cursor:
    pointer;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    9px;

  transition:
    transform 0.18s ease,
    background 0.18s ease,
    opacity 0.18s ease;
}


.cover-edit-button:hover:not(:disabled) {
  background:
    rgba(17, 23, 32, 0.76);

  transform:
    translateY(-1px);
}


.cover-edit-button.disabled {
  opacity:
    0.5;
}


.cover-edit-button:disabled {
  cursor:
    wait;
}


.cover-edit-button svg {
  width:
    15px;

  height:
    15px;
}


/* =========================================================
   HERO CONTENT
========================================================= */

.hero-content {
  position:
    relative;

  min-height:
    150px;

  padding:
    78px
    28px
    24px;
}


/* =========================================================
   LOGO
========================================================= */

.company-logo-shell {
  position:
    absolute;

  left:
    28px;

  top:
    -56px;

  width:
    116px;

  height:
    116px;

  padding:
    5px;

  background:
    var(--card-bg);

  border:
    1px solid
    var(--border);

  border-radius:
    29px;

  box-shadow:
    0 11px 30px
    rgba(18, 36, 62, 0.16);

  transition:
    transform 0.18s ease,
    background-color 0.25s ease;
}


.theme-dark
.company-logo-shell {
  box-shadow:
    0 13px 32px
    rgba(0, 0, 0, 0.34);
}


.company-logo-shell.clickable {
  cursor:
    pointer;
}


.company-logo-shell.clickable:hover {
  transform:
    translateY(-2px);
}


.company-logo-shell.uploading {
  pointer-events:
    none;
}


.company-logo,
.company-logo-placeholder {
  width:
    100%;

  height:
    100%;

  border-radius:
    24px;
}


.company-logo {
  display:
    block;

  object-fit:
    cover;
}


.company-logo-placeholder {
  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  color:
    var(--blue);

  background:
    linear-gradient(
      145deg,
      var(--blue-soft),
      var(--purple-soft)
    );

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size:
    31px;
}


.logo-edit-button {
  position:
    absolute;

  right:
    -4px;

  bottom:
    -4px;

  width:
    33px;

  height:
    33px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  color:
    #ffffff;

  background:
    linear-gradient(
      135deg,
      #287be7,
      #536be2
    );

  border:
    4px solid
    var(--card-bg);

  border-radius:
    50%;

  box-shadow:
    0 5px 13px
    rgba(39, 121, 232, 0.28);
}


.logo-edit-button svg {
  width:
    13px;

  height:
    13px;
}


/* =========================================================
   HERO INFO
========================================================= */

.hero-info {
  width:
    100%;
}


.hero-title-row {
  display:
    flex;

  align-items:
    flex-start;

  justify-content:
    space-between;

  gap:
    30px;
}


.hero-name-block {
  min-width:
    0;
}


.hero-name-block h2 {
  margin:
    0;

  color:
    var(--text);

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size:
    24px;

  line-height:
    1.15;

  letter-spacing:
    -0.025em;
}


.hero-meta {
  display:
    flex;

  align-items:
    center;

  flex-wrap:
    wrap;

  gap:
    8px;

  margin-top:
    11px;
}


.location-pill,
.specifics-pill {
  min-height:
    28px;

  display:
    inline-flex;

  align-items:
    center;

  gap:
    6px;

  padding:
    0
    10px;

  border:
    none;

  border-radius:
    9px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    9px;
}


.location-pill {
  color:
    var(--text-secondary);

  background:
    var(--card-soft);

  cursor:
    pointer;

  transition:
    transform 0.18s ease,
    background 0.18s ease;
}


.location-pill:hover {
  transform:
    translateY(-1px);

  background:
    var(--card-hover);
}


.location-pill svg {
  width:
    13px;

  height:
    13px;
}


.specifics-pill {
  color:
    var(--purple);

  background:
    var(--purple-soft);
}


/* =========================================================
   EDITING BUTTON
========================================================= */

.editing-button {
  flex-shrink:
    0;

  min-height:
    38px;

  display:
    inline-flex;

  align-items:
    center;

  justify-content:
    center;

  gap:
    8px;

  padding:
    0
    13px;

  color:
    #ffffff;

  background:
    linear-gradient(
      135deg,
      #287be7,
      #506ce3
    );

  border:
    none;

  border-radius:
    11px;

  cursor:
    pointer;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    9px;

  box-shadow:
    0 7px 16px
    rgba(43, 115, 224, 0.16);

  transition:
    transform 0.18s ease,
    opacity 0.18s ease,
    box-shadow 0.18s ease;
}


.editing-button svg {
  width:
    14px;

  height:
    14px;
}


.editing-button:hover:not(:disabled) {
  transform:
    translateY(-1px);

  box-shadow:
    0 10px 22px
    rgba(43, 115, 224, 0.23);
}


.editing-button.granted {
  color:
    var(--green);

  background:
    var(--green-soft);

  border:
    1px solid
    rgba(44, 154, 105, 0.14);

  box-shadow:
    none;
}


.editing-button.requested {
  color:
    var(--blue);

  background:
    var(--blue-soft);

  border:
    1px solid
    rgba(40, 123, 231, 0.14);

  box-shadow:
    none;
}


.editing-button:disabled {
  cursor:
    default;
}


/* =========================================================
   ACCESS MESSAGE
========================================================= */

.access-message {
  display:
    flex;

  align-items:
    center;

  gap:
    13px;

  margin-bottom:
    22px;

  padding:
    15px
    17px;

  background:
    var(--card-bg);

  border:
    1px solid
    var(--border);

  border-radius:
    15px;

  box-shadow:
    var(--shadow);
}


.access-message-icon {
  width:
    38px;

  height:
    38px;

  flex-shrink:
    0;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  color:
    var(--blue);

  background:
    var(--blue-soft);

  border-radius:
    11px;
}


.access-message-icon svg {
  width:
    19px;

  height:
    19px;
}


.access-message strong,
.access-message span {
  display:
    block;
}


.access-message strong {
  color:
    var(--text);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    10px;
}


.access-message span {
  margin-top:
    3px;

  color:
    var(--text-secondary);

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    9px;

  line-height:
    1.45;
}


/* =========================================================
   CARDS
========================================================= */

.dashboard-card {
  background:
    var(--card-bg);

  border:
    1px solid
    var(--border);

  border-radius:
    22px;

  box-shadow:
    var(--shadow);

  transition:
    background-color 0.25s ease,
    border-color 0.18s ease,
    box-shadow 0.18s ease;
}


.dashboard-card:hover {
  border-color:
    var(--border-hover);

  box-shadow:
    var(--shadow-hover);
}


.main-info-card {
  padding:
    25px;
}


.dashboard-grid {
  display:
    grid;

  grid-template-columns:
    repeat(
      2,
      minmax(0, 1fr)
    );

  gap:
    22px;

  margin-top:
    22px;

  align-items:
    start;
}


.office-card,
.social-card {
  padding:
    23px;
}


.wide-card {
  margin-top:
    22px;

  padding:
    25px;
}


.disabled-card {
  opacity:
    0.62;
}


/* =========================================================
   CARD HEADING
========================================================= */

.card-heading {
  display:
    flex;

  align-items:
    flex-start;

  justify-content:
    space-between;

  gap:
    20px;

  margin-bottom:
    24px;
}


.card-label {
  display:
    block;

  margin-bottom:
    5px;

  color:
    var(--text-muted);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    8px;

  letter-spacing:
    0.08em;
}


.card-heading h3 {
  margin:
    0;

  color:
    var(--text);

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size:
    17px;

  letter-spacing:
    -0.018em;
}


.card-heading p {
  max-width:
    450px;

  margin:
    6px
    0
    0;

  color:
    var(--text-secondary);

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    10px;

  line-height:
    1.55;
}


/* =========================================================
   SECTION ICON
========================================================= */

.section-icon {
  width:
    43px;

  height:
    43px;

  flex-shrink:
    0;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  border-radius:
    13px;
}


.section-icon svg {
  width:
    21px;

  height:
    21px;
}


.section-icon.blue {
  color:
    var(--blue);

  background:
    var(--blue-soft);
}


.section-icon.purple {
  color:
    var(--purple);

  background:
    var(--purple-soft);
}


.section-icon.green {
  color:
    var(--green);

  background:
    var(--green-soft);
}


.section-icon.orange {
  color:
    var(--orange);

  background:
    var(--orange-soft);
}


/* =========================================================
   FORM
========================================================= */

.form-grid {
  display:
    grid;

  grid-template-columns:
    repeat(
      2,
      minmax(0, 1fr)
    );

  gap:
    15px;
}


.form-field {
  min-width:
    0;
}


.form-field.full {
  grid-column:
    1 / -1;
}


.form-field > span {
  display:
    block;

  margin:
    0
    0
    7px
    2px;

  color:
    var(--text-secondary);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    9px;
}


.form-field input,
.form-field textarea {
  width:
    100%;

  box-sizing:
    border-box;

  color:
    var(--text);

  background:
    var(--input-bg);

  border:
    1px solid
    var(--input-border);

  border-radius:
    12px;

  outline:
    none;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    12px;

  transition:
    background 0.18s ease,
    border-color 0.18s ease,
    box-shadow 0.18s ease;
}


.form-field input {
  height:
    45px;

  padding:
    0
    13px;
}


.form-field textarea {
  min-height:
    145px;

  padding:
    13px;

  resize:
    vertical;

  line-height:
    1.5;
}


.form-field input::placeholder,
.form-field textarea::placeholder {
  color:
    var(--input-placeholder);
}


.form-field input:hover:not(:disabled),
.form-field textarea:hover:not(:disabled) {
  border-color:
    var(--border-hover);
}


.form-field input:focus,
.form-field textarea:focus {
  background:
    var(--input-focus);

  border-color:
    rgba(69, 137, 231, 0.56);

  box-shadow:
    0 0 0 4px
    rgba(49, 121, 226, 0.07);
}


.form-field input:disabled,
.form-field textarea:disabled {
  opacity:
    0.55;

  cursor:
    not-allowed;
}


/* =========================================================
   CARD ACTIONS
========================================================= */

.card-actions {
  display:
    flex;

  align-items:
    center;

  justify-content:
    space-between;

  gap:
    18px;

  margin-top:
    22px;

  padding-top:
    18px;

  border-top:
    1px solid
    var(--divider);
}


.save-state {
  color:
    var(--green);

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    9px;
}


.save-state.changed {
  color:
    var(--orange);
}


/* =========================================================
   BUTTONS
========================================================= */

.primary-button,
.secondary-button {
  min-height:
    42px;

  display:
    inline-flex;

  align-items:
    center;

  justify-content:
    center;

  gap:
    8px;

  padding:
    0
    16px;

  border-radius:
    12px;

  cursor:
    pointer;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    10px;

  transition:
    transform 0.18s ease,
    opacity 0.18s ease,
    box-shadow 0.18s ease,
    background 0.18s ease;
}


.primary-button {
  color:
    #ffffff;

  background:
    linear-gradient(
      135deg,
      #287be7,
      #506ce3
    );

  border:
    none;

  box-shadow:
    0 7px 16px
    rgba(43, 115, 224, 0.18);
}


.secondary-button {
  color:
    var(--blue);

  background:
    var(--blue-soft);

  border:
    1px solid
    rgba(77, 141, 226, 0.16);
}


.primary-button:hover:not(:disabled),
.secondary-button:hover:not(:disabled) {
  transform:
    translateY(-1px);
}


.primary-button:disabled,
.secondary-button:disabled {
  opacity:
    0.4;

  cursor:
    default;

  transform:
    none;

  box-shadow:
    none;
}


/* =========================================================
   OFFICE
========================================================= */

.current-address {
  display:
    flex;

  align-items:
    center;

  gap:
    11px;

  padding:
    12px;

  margin-bottom:
    14px;

  background:
    var(--card-soft);

  border:
    1px solid
    var(--border);

  border-radius:
    13px;
}


.address-icon {
  width:
    34px;

  height:
    34px;

  flex-shrink:
    0;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  color:
    var(--text-secondary);

  background:
    var(--card-bg);

  border:
    1px solid
    var(--border);

  border-radius:
    10px;
}


.address-icon svg {
  width:
    17px;

  height:
    17px;
}


.current-address span,
.current-address strong {
  display:
    block;
}


.current-address span {
  margin-bottom:
    2px;

  color:
    var(--text-muted);

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    8px;
}


.current-address strong {
  color:
    var(--text);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    10px;

  font-weight:
    normal;

  line-height:
    1.4;
}


.map-container {
  overflow:
    hidden;

  border:
    1px solid
    var(--border);

  border-radius:
    15px;
}


/* =========================================================
   SOCIAL
========================================================= */

.social-grid {
  display:
    flex;

  flex-direction:
    column;

  gap:
    9px;
}


.social-field {
  min-height:
    50px;

  display:
    flex;

  align-items:
    center;

  gap:
    10px;

  padding:
    7px
    10px;

  background:
    var(--card-soft);

  border:
    1px solid
    var(--border-strong);

  border-radius:
    12px;

  transition:
    border-color 0.18s ease,
    background 0.18s ease;
}


.social-field:focus-within {
  border-color:
    rgba(69, 137, 231, 0.48);

  background:
    var(--input-focus);
}


.social-icon-shell {
  width:
    33px;

  height:
    33px;

  flex-shrink:
    0;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  opacity:
    0.5;

  background:
    var(--card-hover);

  border-radius:
    10px;

  transition:
    opacity 0.18s ease,
    background 0.18s ease;
}


.social-icon-shell.active {
  opacity:
    1;

  background:
    var(--card-bg);
}


.social-icon-shell img {
  width:
    17px;

  height:
    17px;

  object-fit:
    contain;
}


.social-field input {
  width:
    100%;

  padding:
    0;

  color:
    var(--text);

  background:
    transparent;

  border:
    none;

  outline:
    none;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    9px;
}


.social-field input::placeholder {
  color:
    var(--input-placeholder);
}


.social-field input:disabled {
  cursor:
    not-allowed;
}


/* =========================================================
   GALLERY
========================================================= */

.images-row {
  display:
    grid;

  grid-template-columns:
    repeat(
      3,
      minmax(0, 1fr)
    );

  gap:
    13px;
}


.image-slot {
  position:
    relative;

  min-width:
    0;

  height:
    180px;

  padding:
    0;

  overflow:
    hidden;

  color:
    var(--text);

  background:
    var(--card-soft);

  border:
    1.5px dashed
    var(--input-border);

  border-radius:
    17px;

  cursor:
    pointer;

  transition:
    transform 0.18s ease,
    border-color 0.18s ease,
    box-shadow 0.18s ease;
}


.image-slot:hover:not(.disabled) {
  transform:
    translateY(-2px);

  border-color:
    var(--blue);

  box-shadow:
    0 8px 22px
    rgba(34, 64, 104, 0.07);
}


.image-slot.filled {
  border-style:
    solid;

  border-color:
    var(--border);
}


.image-slot.disabled {
  opacity:
    0.55;

  cursor:
    not-allowed;
}


.img-wrapper {
  position:
    relative;

  width:
    100%;

  height:
    100%;
}


.slot-img {
  width:
    100%;

  height:
    100%;

  display:
    block;

  object-fit:
    cover;
}


.slot-placeholder {
  width:
    100%;

  height:
    100%;

  display:
    flex;

  flex-direction:
    column;

  align-items:
    center;

  justify-content:
    center;

  gap:
    8px;

  color:
    var(--text-secondary);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    9px;
}


.plus {
  width:
    38px;

  height:
    38px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  color:
    var(--blue);

  background:
    var(--blue-soft);

  border-radius:
    12px;

  font-family:
    sans-serif;

  font-size:
    23px;

  font-weight:
    200;
}


.delete-btn {
  position:
    absolute;

  top:
    9px;

  right:
    9px;

  width:
    29px;

  height:
    29px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  padding:
    0;

  color:
    #ffffff;

  background:
    rgba(18, 24, 32, 0.64);

  border:
    none;

  border-radius:
    50%;

  backdrop-filter:
    blur(10px);

  cursor:
    pointer;

  transition:
    transform 0.18s ease,
    background 0.18s ease;
}


.delete-btn:hover {
  background:
    var(--red);

  transform:
    scale(1.07);
}


.delete-btn svg {
  width:
    13px;

  height:
    13px;
}


.img-wrapper.deleting
.slot-img {
  opacity:
    0.42;

  filter:
    blur(2px);
}


.image-upload-overlay {
  position:
    absolute;

  inset:
    0;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  background:
    rgba(17, 24, 35, 0.36);

  backdrop-filter:
    blur(2px);
}


/* =========================================================
   PERKS
========================================================= */

.perk-grid {
  display:
    flex;

  flex-wrap:
    wrap;

  gap:
    8px;
}


.perk {
  min-height:
    34px;

  display:
    inline-flex;

  align-items:
    center;

  gap:
    6px;

  padding:
    0
    11px;

  color:
    var(--text-secondary);

  background:
    var(--card-soft);

  border:
    1px solid
    var(--input-border);

  border-radius:
    10px;

  cursor:
    pointer;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    9px;

  transition:
    transform 0.18s ease,
    background 0.18s ease,
    color 0.18s ease,
    border-color 0.18s ease;
}


.perk:hover:not(:disabled) {
  transform:
    translateY(-1px);

  background:
    var(--card-hover);
}


.perk.selected {
  color:
    var(--blue);

  background:
    var(--blue-soft);

  border-color:
    rgba(65, 130, 225, 0.18);
}


.perk:disabled {
  cursor:
    not-allowed;
}


.perk-check {
  width:
    0;

  overflow:
    hidden;

  opacity:
    0;

  color:
    var(--blue);

  transition:
    width 0.15s ease,
    opacity 0.15s ease;
}


.perk-check.visible {
  width:
    10px;

  opacity:
    1;
}


/* =========================================================
   LOADERS
========================================================= */

.button-spinner,
.small-spinner,
.tiny-spinner {
  display:
    inline-block;

  flex-shrink:
    0;

  border-radius:
    50%;

  animation:
    spinner-rotation
    0.7s
    linear
    infinite;
}


.button-spinner {
  width:
    15px;

  height:
    15px;

  border:
    2px solid
    rgba(255, 255, 255, 0.30);

  border-top-color:
    #ffffff;
}


.button-spinner.dark {
  border-color:
    rgba(52, 117, 210, 0.18);

  border-top-color:
    var(--blue);
}


.small-spinner {
  width:
    14px;

  height:
    14px;

  border:
    2px solid
    rgba(255, 255, 255, 0.28);

  border-top-color:
    #ffffff;
}


.tiny-spinner,
.mini-loader {
  width:
    13px;

  height:
    13px;

  border:
    2px solid
    rgba(255, 255, 255, 0.26);

  border-top-color:
    #ffffff;

  border-radius:
    50%;

  animation:
    spinner-rotation
    0.7s
    linear
    infinite;
}


@keyframes spinner-rotation {
  to {
    transform:
      rotate(360deg);
  }
}


/* =========================================================
   HIDDEN
========================================================= */

.hidden {
  position:
    absolute;

  width:
    1px;

  height:
    1px;

  opacity:
    0;

  pointer-events:
    none;
}


/* =========================================================
   SKELETON
========================================================= */

.loading-shell {
  min-height:
    650px;
}


.skeleton {
  position:
    relative;

  overflow:
    hidden;

  background:
    var(--skeleton);

  border-radius:
    20px;
}


.skeleton::after {
  content:
    "";

  position:
    absolute;

  inset:
    0;

  transform:
    translateX(-100%);

  background:
    linear-gradient(
      90deg,
      transparent,
      var(--skeleton-highlight),
      transparent
    );

  animation:
    skeleton-shimmer
    1.4s
    infinite;
}


.hero-skeleton {
  height:
    390px;
}


.loading-grid {
  display:
    grid;

  grid-template-columns:
    repeat(
      2,
      minmax(0, 1fr)
    );

  gap:
    22px;

  margin-top:
    22px;
}


.card-skeleton {
  height:
    300px;
}


.wide-skeleton {
  height:
    220px;

  margin-top:
    22px;
}


@keyframes skeleton-shimmer {
  100% {
    transform:
      translateX(100%);
  }
}


/* =========================================================
   MOBILE
========================================================= */

@media (max-width: 700px) {

  .page-shell {
    width:
      calc(100% - 24px);

    padding:
      20px
      0
      28px;
  }


  .page-intro {
    flex-direction:
      column;

    gap:
      16px;

    margin-bottom:
      20px;
  }


  .page-intro h1 {
    font-size:
      24px;
  }


  .access-indicator {
    align-self:
      flex-start;
  }


  .cover-area {
    height:
      210px;
  }


  .cover-edit-button {
    right:
      12px;

    bottom:
      12px;
  }


  .hero-content {
    min-height:
      145px;

    padding:
      70px
      18px
      20px;
  }


  .company-logo-shell {
    left:
      18px;

    top:
      -50px;

    width:
      100px;

    height:
      100px;

    border-radius:
      25px;
  }


  .company-logo,
  .company-logo-placeholder {
    border-radius:
      20px;
  }


  .hero-title-row {
    flex-direction:
      column;

    gap:
      16px;
  }


  .hero-name-block h2 {
    font-size:
      21px;
  }


  .editing-button {
    align-self:
      flex-start;
  }


  .dashboard-grid {
    grid-template-columns:
      1fr;
  }


  .main-info-card,
  .office-card,
  .social-card,
  .wide-card {
    padding:
      19px;
  }


  .form-grid {
    grid-template-columns:
      1fr;
  }


  .form-field.full {
    grid-column:
      auto;
  }


  .images-row {
    grid-template-columns:
      1fr;
  }


  .image-slot {
    height:
      190px;
  }


  .card-actions {
    align-items:
      stretch;

    flex-direction:
      column;
  }


  .primary-button,
  .secondary-button {
    width:
      100%;
  }


  .card-heading {
    gap:
      12px;
  }


  .section-icon {
    width:
      39px;

    height:
      39px;
  }


  .loading-grid {
    grid-template-columns:
      1fr;
  }

}


/* =========================================================
   SMALL MOBILE
========================================================= */

@media (max-width: 430px) {

  .page-shell {
    width:
      calc(100% - 20px);
  }


  .page-intro h1 {
    font-size:
      22px;
  }


  .cover-area {
    height:
      190px;
  }


  .cover-edit-button {
    min-height:
      34px;

    font-size:
      8px;
  }


  .hero-name-block h2 {
    font-size:
      20px;
  }


  .company-logo-shell {
    width:
      94px;

    height:
      94px;
  }


  .dashboard-card {
    border-radius:
      18px;
  }


  .card-heading h3 {
    font-size:
      16px;
  }


  .perk {
    min-height:
      32px;

    padding:
      0
      10px;

    font-size:
      8px;
  }

}

</style>